import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api, AuthRequiredError, getApiSourceInfo, setApiReportingTimezone } from "./api.js";
import { FilterBar } from "./components/FilterBar.js";
import { TabNav } from "./components/TabNav.js";
import { useAnalyticsData } from "./hooks.js";
import { type ChartTableSelection,chartTablesEqual } from "./lib/chartTables.js";
import { navigateOrOpenNewTab } from "./lib/dashboardNav.js";
import { formatEventTimeLocal } from "./lib/dashboardTime.js";
import {
  buildDashboardUrl,
  type DashboardUrlState,
  defaultFilters,
  parseDashboardUrlState,
} from "./lib/dashboardUrl.js";
import { normalizeFilterDateTime } from "./lib/filterDates.js";
import { buildRawEventsUrlFromOptions } from "./lib/rawEventsUrl.js";
import {
  formatTimezoneLabel,
  formatTimezoneOptionLabel,
  REPORTING_TIMEZONE_OPTIONS,
  resolveReportingTimezone,
  saveStoredReportingTimezone,
} from "./lib/reportingTimezone.js";
import { ReportingTimezoneProvider } from "./lib/ReportingTimezoneContext.js";
import { AcquisitionPanel } from "./panels/AcquisitionPanel.js";
import { EngagementPanel } from "./panels/EngagementPanel.js";
import { EventsPanel } from "./panels/EventsPanel.js";
import { HealthPanel } from "./panels/HealthPanel.js";
import { OverviewPanel } from "./panels/OverviewPanel.js";
import { PresencePanel } from "./panels/PresencePanel.js";
import { RetentionPanel } from "./panels/RetentionPanel.js";
import { SessionsPanel } from "./panels/SessionsPanel.js";
import type {
  AcquisitionData,
  EngagementData,
  EventsData,
  EventsSortColumn,
  EventsSortDir,
  FilterState,
  HealthData,
  InteractionSessionCohort,
  MeResponse,
  OpenRawEventsHandler,
  OverviewData,
  PresenceData,
  RetentionData,
  SessionsData,
  TabId,
} from "./types.js";

/**
 * Adds the current dashboard URL as `return_to`, so the portal sends the user back here — to the
 * tab and filters they were on — rather than leaving them on the admin dashboard. The portal
 * allowlists the destination to `*.teamtable.co` before acting on it.
 */
function signInHref(signInUrl: string): string {
  try {
    const url = new URL(signInUrl);
    url.searchParams.set("return_to", window.location.href);
    return url.toString();
  } catch {
    return signInUrl;
  }
}

export function App() {
  const initial = parseDashboardUrlState();
  const [tab, setTab] = useState<TabId>(initial.tab);
  const [filters, setFilters] = useState<FilterState>(initial.filters);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [authError, setAuthError] = useState<AuthRequiredError | null>(null);
  const [signInUrl, setSignInUrl] = useState<string | null>(null);

  const [eventsOffset, setEventsOffset] = useState(initial.eventsOffset);
  const [eventsPageSize, setEventsPageSize] = useState(initial.eventsPageSize);
  const [eventsEventName, setEventsEventName] = useState(initial.eventsEventName);
  const [eventsPropertiesSearch, setEventsPropertiesSearch] = useState(initial.eventsPropertiesSearch);
  const [eventsPropertiesSearch2, setEventsPropertiesSearch2] = useState(initial.eventsPropertiesSearch2);
  const [eventsClientErrorBucket, setEventsClientErrorBucket] = useState<"" | "warning" | "error">(
    initial.eventsClientErrorBucket,
  );
  const [eventsInteractionSessionId, setEventsInteractionSessionId] = useState(
    initial.eventsInteractionSessionId,
  );
  const [eventsInteractionSessionCohort, setEventsInteractionSessionCohort] = useState<
    "" | InteractionSessionCohort
  >(initial.eventsInteractionSessionCohort);
  const [eventsShowTemperature, setEventsShowTemperature] = useState(initial.eventsShowTemperature);
  const [eventsShowUptimePulse, setEventsShowUptimePulse] = useState(initial.eventsShowUptimePulse);
  const [eventsHideAttractBasics, setEventsHideAttractBasics] = useState(initial.eventsHideAttractBasics);
  const [eventsDedupePopupTapMissed, setEventsDedupePopupTapMissed] = useState(
    initial.eventsDedupePopupTapMissed,
  );
  const [eventsTempCelsiusMin, setEventsTempCelsiusMin] = useState<number | undefined>(
    initial.eventsTempCelsiusMin,
  );
  const [eventsTempCelsiusMaxExclusive, setEventsTempCelsiusMaxExclusive] = useState<
    number | undefined
  >(initial.eventsTempCelsiusMaxExclusive);
  const [eventsSort, setEventsSort] = useState<EventsSortColumn>(initial.eventsSort);
  const [eventsSortDir, setEventsSortDir] = useState<EventsSortDir>(initial.eventsSortDir);
  const [sessionsLongOnly, setSessionsLongOnly] = useState(initial.sessionsLongOnly);
  const [sessionsPaidOnly, setSessionsPaidOnly] = useState(initial.sessionsPaidOnly);
  const [sessionsListOffset, setSessionsListOffset] = useState(initial.sessionsListOffset);
  const [sessionsListLimit, setSessionsListLimit] = useState(initial.sessionsListLimit);
  const [reportingTimezoneOption, setReportingTimezoneOption] = useState(
    initial.reportingTimezoneOption,
  );
  const [chartTables, setChartTablesState] = useState<ChartTableSelection>(initial.chartTables);
  const setChartTables = useCallback((next: ChartTableSelection) => {
    setChartTablesState((prev) => (chartTablesEqual(prev, next) ? prev : next));
  }, []);
  const [chartFullWeeks, setChartFullWeeks] = useState(initial.chartFullWeeks);
  const [localNow, setLocalNow] = useState(() =>
    formatEventTimeLocal(new Date(), resolveReportingTimezone(initial.reportingTimezoneOption)),
  );
  const resolvedReportingTimezone = resolveReportingTimezone(reportingTimezoneOption);
  const urlHistoryMode = useRef<"replace" | "push">("replace");

  /**
   * Establishes who the visitor is before any panel renders. A rejection here is the normal
   * signed-out path, not a failure — the shell swaps to a sign-in prompt.
   */
  useEffect(() => {
    let cancelled = false;
    api
      .me()
      .then((profile) => {
        if (!cancelled) {
          setMe(profile);
          setAuthError(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof AuthRequiredError) setAuthError(err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /** Only needed while signed out, and deliberately unauthenticated so it works in that state. */
  useEffect(() => {
    if (!authError || signInUrl) return;
    api
      .signInUrl()
      .then(setSignInUrl)
      .catch(() => {});
  }, [authError, signInUrl]);

  const requestHistoryPush = useCallback(() => {
    urlHistoryMode.current = "push";
  }, []);

  const applyDashboardUrlState = useCallback((s: ReturnType<typeof parseDashboardUrlState>) => {
    setTab(s.tab);
    setFilters(s.filters);
    setEventsOffset(s.eventsOffset);
    setEventsPageSize(s.eventsPageSize);
    setEventsEventName(s.eventsEventName);
    setEventsPropertiesSearch(s.eventsPropertiesSearch);
    setEventsPropertiesSearch2(s.eventsPropertiesSearch2);
    setEventsClientErrorBucket(s.eventsClientErrorBucket);
    setEventsInteractionSessionId(s.eventsInteractionSessionId);
    setEventsInteractionSessionCohort(s.eventsInteractionSessionCohort);
    setEventsShowTemperature(s.eventsShowTemperature);
    setEventsShowUptimePulse(s.eventsShowUptimePulse);
    setEventsHideAttractBasics(s.eventsHideAttractBasics);
    setEventsDedupePopupTapMissed(s.eventsDedupePopupTapMissed);
    setEventsTempCelsiusMin(s.eventsTempCelsiusMin);
    setEventsTempCelsiusMaxExclusive(s.eventsTempCelsiusMaxExclusive);
    setEventsSort(s.eventsSort);
    setEventsSortDir(s.eventsSortDir);
    setSessionsLongOnly(s.sessionsLongOnly);
    setSessionsPaidOnly(s.sessionsPaidOnly);
    setSessionsListOffset(s.sessionsListOffset);
    setSessionsListLimit(s.sessionsListLimit);
    setReportingTimezoneOption(s.reportingTimezoneOption);
    setChartTables(s.chartTables);
    setChartFullWeeks(s.chartFullWeeks);
  }, [setChartTables]);

  const navigateToRawEvents: OpenRawEventsHandler = useCallback((opts, e) => {
    if (e && navigateOrOpenNewTab(buildRawEventsUrlFromOptions(filters, opts), e) === "new") {
      return;
    }
    requestHistoryPush();
    setTab("events");
    setEventsEventName(opts.eventName);
    setEventsPropertiesSearch((opts.propertiesContains ?? "").trim().slice(0, 500));
    setEventsPropertiesSearch2((opts.propertiesContains2 ?? "").trim().slice(0, 500));
    setEventsOffset(0);
    if (
      opts.eventName === "client_error" &&
      (opts.clientErrorBucket === "warning" || opts.clientErrorBucket === "error")
    ) {
      setEventsClientErrorBucket(opts.clientErrorBucket);
    } else {
      setEventsClientErrorBucket("");
    }
    setEventsInteractionSessionId((opts.interactionSessionId ?? "").trim().slice(0, 128));
    setEventsInteractionSessionCohort(opts.interactionSessionCohort ?? "");
    if (opts.showTemperatureEvents !== undefined) {
      setEventsShowTemperature(opts.showTemperatureEvents);
    }
    setEventsTempCelsiusMin(opts.tempCelsiusMin);
    setEventsTempCelsiusMaxExclusive(opts.tempCelsiusMaxExclusive);
    setFilters((f) => {
      let next: FilterState = { ...f };
      if (opts.gameUrl !== undefined) {
        next = { ...next, gameUrl: opts.gameUrl === "all" ? "all" : opts.gameUrl };
      }
      if (opts.venueId !== undefined) {
        next = {
          ...next,
          venueId: opts.venueId === "all" ? "all" : opts.venueId,
          // Clear device when switching venue so a stale device_id does not zero results.
          deviceId: "all",
        };
      }
      if (opts.deviceId !== undefined) {
        next = { ...next, deviceId: opts.deviceId === "all" ? "all" : opts.deviceId };
      }
      if (opts.drillDownFrom && opts.drillDownTo) {
        next = {
          ...next,
          timeframe: "custom",
          from: normalizeFilterDateTime(opts.drillDownFrom, next.from, "from"),
          to: normalizeFilterDateTime(opts.drillDownTo, next.to, "to"),
        };
      }
      return next;
    });
  }, [filters, requestHistoryPush]);

  const { data, loading, error, authError: dataAuthError } = useAnalyticsData(tab, filters, {
    limit: eventsPageSize,
    offset: eventsOffset,
    eventName: eventsEventName,
    propertiesContains: eventsPropertiesSearch,
    propertiesContains2: eventsPropertiesSearch2,
    clientErrorBucket:
      eventsEventName === "client_error" && (eventsClientErrorBucket === "warning" || eventsClientErrorBucket === "error")
        ? eventsClientErrorBucket
        : undefined,
    interactionSessionId: eventsInteractionSessionId || undefined,
    interactionSessionCohort: eventsInteractionSessionCohort || undefined,
    sessionsListLimit,
    sessionsListOffset,
    showTemperatureEvents: eventsShowTemperature,
    showUptimePulse: eventsShowUptimePulse,
    hideAttractBasics: eventsHideAttractBasics,
    dedupePopupTapMissed: eventsDedupePopupTapMissed,
    tempCelsiusMin: eventsTempCelsiusMin,
    tempCelsiusMaxExclusive: eventsTempCelsiusMaxExclusive,
    sortBy: eventsSort,
    sortDir: eventsSortDir,
    sessionsLongOnly,
    sessionsPaidOnly,
    reportingTimezone: resolvedReportingTimezone,
  });

  const handleResetFilters = () => {
    setFilters({ ...defaultFilters });
    setChartTables("all");
    setChartFullWeeks(false);
    setEventsOffset(0);
    setEventsPageSize(50);
    setEventsEventName("all");
    setEventsPropertiesSearch("");
    setEventsPropertiesSearch2("");
    setEventsClientErrorBucket("");
    setEventsInteractionSessionId("");
    setEventsInteractionSessionCohort("");
    setEventsShowTemperature(false);
    setEventsShowUptimePulse(false);
    setEventsHideAttractBasics(true);
    setEventsDedupePopupTapMissed(true);
    setEventsTempCelsiusMin(undefined);
    setEventsTempCelsiusMaxExclusive(undefined);
    setEventsSort("client_timestamp");
    setEventsSortDir("desc");
    setSessionsLongOnly(false);
    setSessionsPaidOnly(false);
    setSessionsListOffset(0);
    setSessionsListLimit(50);
  };

  const handleTabChange = (nextTab: TabId) => {
    if (nextTab !== tab) requestHistoryPush();
    setTab(nextTab);
    if (nextTab !== "events") {
      setEventsOffset(0);
      setEventsEventName("all");
      setEventsPropertiesSearch("");
      setEventsPropertiesSearch2("");
      setEventsClientErrorBucket("");
      setEventsInteractionSessionId("");
      setEventsInteractionSessionCohort("");
      setEventsTempCelsiusMin(undefined);
      setEventsTempCelsiusMaxExclusive(undefined);
    }
    if (nextTab !== "sessions") {
      setSessionsListOffset(0);
    }
  };

  const source = getApiSourceInfo();

  useEffect(() => {
    setApiReportingTimezone(resolvedReportingTimezone);
  }, [resolvedReportingTimezone]);

  useEffect(() => {
    const timer = setInterval(() => {
      setLocalNow(formatEventTimeLocal(new Date(), resolvedReportingTimezone));
    }, 1000);
    return () => clearInterval(timer);
  }, [resolvedReportingTimezone]);

  useEffect(() => {
    const onPopState = () => {
      applyDashboardUrlState(parseDashboardUrlState());
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [applyDashboardUrlState]);

  /** Dashboard URL fields shared by the URL-sync effect and each tab's shareable `href` (see `tabHref`). */
  const dashboardUrlStateWithoutTab: Omit<DashboardUrlState, "tab"> = useMemo(
    () => ({
      filters,
      eventsOffset,
      eventsPageSize,
      eventsEventName,
      eventsPropertiesSearch,
      eventsPropertiesSearch2,
      eventsClientErrorBucket,
      eventsInteractionSessionId,
      eventsInteractionSessionCohort,
      sessionsListOffset,
      sessionsListLimit,
      eventsShowTemperature,
      eventsShowUptimePulse,
      eventsHideAttractBasics,
      eventsDedupePopupTapMissed,
      eventsTempCelsiusMin,
      eventsTempCelsiusMaxExclusive,
      eventsSort,
      eventsSortDir,
      sessionsLongOnly,
      sessionsPaidOnly,
      reportingTimezoneOption,
      chartTables,
      chartFullWeeks,
    }),
    [
      filters,
      eventsOffset,
      eventsPageSize,
      eventsEventName,
      eventsPropertiesSearch,
      eventsPropertiesSearch2,
      eventsClientErrorBucket,
      eventsInteractionSessionId,
      eventsInteractionSessionCohort,
      sessionsListOffset,
      sessionsListLimit,
      eventsShowTemperature,
      eventsShowUptimePulse,
      eventsHideAttractBasics,
      eventsDedupePopupTapMissed,
      eventsTempCelsiusMin,
      eventsTempCelsiusMaxExclusive,
      eventsSort,
      eventsSortDir,
      sessionsLongOnly,
      sessionsPaidOnly,
      reportingTimezoneOption,
      chartTables,
      chartFullWeeks,
    ],
  );

  const tabHref = useCallback(
    (t: TabId) => buildDashboardUrl({ ...dashboardUrlStateWithoutTab, tab: t }),
    [dashboardUrlStateWithoutTab],
  );

  const navigateToSessions = useCallback(
    (e?: Pick<MouseEvent, "button" | "ctrlKey" | "metaKey"> | null, opts?: {
      drillDownFrom?: string;
      drillDownTo?: string;
      venueId?: "all" | string;
    }) => {
      let nextFilters = filters;
      if (opts?.venueId !== undefined || (opts?.drillDownFrom && opts?.drillDownTo)) {
        nextFilters = { ...filters };
        if (opts.venueId !== undefined) {
          nextFilters = {
            ...nextFilters,
            venueId: opts.venueId === "all" ? "all" : opts.venueId,
            deviceId: "all",
          };
        }
        if (opts.drillDownFrom && opts.drillDownTo) {
          nextFilters = {
            ...nextFilters,
            timeframe: "custom" as const,
            from: normalizeFilterDateTime(opts.drillDownFrom, filters.from, "from"),
            to: normalizeFilterDateTime(opts.drillDownTo, filters.to, "to"),
          };
        }
      }
      const href = buildDashboardUrl({
        ...dashboardUrlStateWithoutTab,
        filters: nextFilters,
        tab: "sessions",
        sessionsListOffset: 0,
      });
      if (e && navigateOrOpenNewTab(href, e) === "new") {
        return;
      }
      requestHistoryPush();
      if (nextFilters !== filters) {
        setFilters(nextFilters);
      }
      setTab("sessions");
      setSessionsListOffset(0);
    },
    [dashboardUrlStateWithoutTab, filters, requestHistoryPush],
  );

  useEffect(() => {
    const next = buildDashboardUrl({ ...dashboardUrlStateWithoutTab, tab });
    const sync = urlHistoryMode.current === "push" ? history.pushState : history.replaceState;
    sync.call(window.history, null, "", next);
    urlHistoryMode.current = "replace";
  }, [tab, dashboardUrlStateWithoutTab]);

  /** A panel rejection (e.g. a bookmarked `fleet_id` that is not the caller's) gates the shell too. */
  const effectiveAuthError = authError ?? dataAuthError;

  if (effectiveAuthError) {
    return (
      <div className="mx-auto flex min-h-screen max-w-[560px] flex-col justify-center px-5 py-10">
        <div className="rounded-xl border border-border bg-panel p-6">
          <h1 className="m-0 mb-2 text-[22px] font-bold text-text-primary">
            TeamTable Analytics
          </h1>
          <p className="mb-4 text-[14px] text-text-secondary">
            {effectiveAuthError.status === 403
              ? "That fleet is not yours. Pick a different fleet, or sign in with an account that has access."
              : "Sign in with your TeamTable account to see your fleet's analytics."}
          </p>
          {signInUrl && (
            <a
              className="inline-block rounded-lg border border-border-subtle bg-panel-2 px-4 py-2 text-[14px] text-text-primary"
              href={signInHref(signInUrl)}
            >
              Sign in
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <ReportingTimezoneProvider timeZone={resolvedReportingTimezone}>
    <div className="mx-auto max-w-[1400px] px-5 py-[18px] pb-10">
      <div className="no-print mb-[10px] flex flex-wrap items-center justify-between gap-3">
        <h1 className="m-0 text-[28px] font-bold text-text-primary">TeamTable Analytics</h1>
        <div className="flex flex-wrap items-center gap-2">
          <div className="rounded-lg border border-border-subtle bg-panel-3 px-3 py-1.5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[10px] uppercase tracking-wide text-text-secondary">
                  Reporting timezone
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[12px] font-semibold text-text-primary">
                    {formatTimezoneLabel(resolvedReportingTimezone)}
                  </span>
                  <select
                    className="rounded border border-border-subtle bg-panel-2 px-1.5 py-0.5 text-[11px] text-text-primary"
                    value={reportingTimezoneOption}
                    onChange={(e) => {
                      const next = e.target.value;
                      setReportingTimezoneOption(next);
                      saveStoredReportingTimezone(next);
                    }}
                    aria-label="Reporting timezone"
                  >
                    {REPORTING_TIMEZONE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {formatTimezoneOptionLabel(opt)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <span className="shrink-0 whitespace-nowrap text-[11px] text-text-secondary">
                Local now: {localNow}
              </span>
            </div>
          </div>
          <span className="rounded-full border border-border-subtle bg-panel-3 px-3 py-1 text-xs font-semibold text-text-secondary">
            Source: {source.label}
          </span>
          <span className="rounded-full border border-border-subtle bg-panel-3 px-3 py-1 font-mono text-[11px] text-text-secondary">
            {source.baseUrl}
          </span>
        </div>
      </div>

      <div className="no-print">
        <FilterBar
          filters={filters}
          reportingTimezoneLabel={formatTimezoneLabel(resolvedReportingTimezone)}
          fleets={me?.fleets ?? []}
          onFiltersChange={setFilters}
          onReset={handleResetFilters}
          disableGame={tab === "presence"}
        />
      </div>

      <TabNav activeTab={tab} tabHref={tabHref} onTabChange={handleTabChange} />

      <div className="rounded-xl border border-border bg-panel p-3">
        {loading && (
          <div className="py-5 text-center text-[13px] text-text-secondary">Loading...</div>
        )}
        {error && (
          <div className="py-5 text-center text-[13px] text-err">Error: {error}</div>
        )}
        {!loading && !error && tab === "overview" && (
          <OverviewPanel
            data={data as OverviewData | null}
            filters={filters}
            reportingTimezone={resolvedReportingTimezone}
            onOpenRawEvents={navigateToRawEvents}
            onOpenSessions={navigateToSessions}
            chartTables={chartTables}
            onChartTablesChange={setChartTables}
            chartFullWeeks={chartFullWeeks}
            onChartFullWeeksChange={setChartFullWeeks}
          />
        )}
        {!loading && !error && tab === "health" && (
          <HealthPanel
            data={data as HealthData | null}
            filters={filters}
            onOpenRawEvents={navigateToRawEvents}
            chartTables={chartTables}
            onChartTablesChange={setChartTables}
            chartFullWeeks={chartFullWeeks}
            onChartFullWeeksChange={setChartFullWeeks}
          />
        )}
        {!loading && !error && tab === "acquisition" && (
          <AcquisitionPanel
            data={data as AcquisitionData | null}
            filters={filters}
            reportingTimezone={resolvedReportingTimezone}
            onOpenRawEvents={navigateToRawEvents}
            onOpenSessions={navigateToSessions}
            chartTables={chartTables}
            onChartTablesChange={setChartTables}
            chartFullWeeks={chartFullWeeks}
            onChartFullWeeksChange={setChartFullWeeks}
          />
        )}
        {!loading && !error && tab === "engagement" && (
          <EngagementPanel
            data={data as EngagementData | null}
            filters={filters}
            onOpenRawEvents={navigateToRawEvents}
          />
        )}
        {!loading && !error && tab === "retention" && (
          <RetentionPanel
            data={data as RetentionData | null}
            filters={filters}
            reportingTimezone={resolvedReportingTimezone}
            onOpenRawEvents={navigateToRawEvents}
            onOpenSessions={navigateToSessions}
            chartTables={chartTables}
            onChartTablesChange={setChartTables}
            chartFullWeeks={chartFullWeeks}
            onChartFullWeeksChange={setChartFullWeeks}
          />
        )}
        {!loading && !error && tab === "sessions" && (
          <SessionsPanel
            data={data as SessionsData | null}
            filters={filters}
            listOffset={sessionsListOffset}
            listLimit={sessionsListLimit}
            longOnly={sessionsLongOnly}
            paidOnly={sessionsPaidOnly}
            onLongOnlyChange={(on) => {
              setSessionsLongOnly(on);
              setSessionsListOffset(0);
            }}
            onPaidOnlyChange={(on) => {
              setSessionsPaidOnly(on);
              setSessionsListOffset(0);
            }}
            onListPageSizeChange={(n) => {
              setSessionsListLimit(n);
              setSessionsListOffset(0);
            }}
            onListPrevPage={() =>
              setSessionsListOffset((v) => Math.max(0, v - sessionsListLimit))
            }
            onListNextPage={() => setSessionsListOffset((v) => v + sessionsListLimit)}
            onGoToPage={(page) => {
              const d = data as SessionsData | null;
              if (!d) return;
              const lim = sessionsListLimit;
              const totalPages = Math.max(1, Math.ceil(d.longSessions.total / lim));
              const p = Math.min(Math.max(1, page), totalPages);
              setSessionsListOffset((p - 1) * lim);
            }}
            onOpenRawEvents={navigateToRawEvents}
          />
        )}
        {!loading && !error && tab === "presence" && (
          <PresencePanel data={data as PresenceData | null} onOpenRawEvents={navigateToRawEvents} />
        )}
        {!loading && !error && tab === "events" && (
          <EventsPanel
            data={data as EventsData | null}
            filters={filters}
            pageSize={eventsPageSize}
            onPageSizeChange={(n) => {
              setEventsPageSize(n);
              setEventsOffset(0);
            }}
            onPrevPage={() => setEventsOffset((v) => Math.max(0, v - eventsPageSize))}
            onNextPage={() => setEventsOffset((v) => v + eventsPageSize)}
            onGoToPage={(page) => {
              const d = data as EventsData | null;
              if (!d) return;
              const lim = eventsPageSize;
              const totalPages = Math.max(1, Math.ceil(d.total / lim));
              const p = Math.min(Math.max(1, page), totalPages);
              setEventsOffset((p - 1) * lim);
            }}
            eventName={eventsEventName}
            onEventNameChange={(value) => {
              setEventsEventName(value);
              setEventsOffset(0);
              if (value !== "client_error") setEventsClientErrorBucket("");
            }}
            propertiesSearch={eventsPropertiesSearch}
            onPropertiesSearchChange={(value) => {
              setEventsPropertiesSearch(value.trim().slice(0, 500));
              setEventsOffset(0);
            }}
            propertiesSearch2={eventsPropertiesSearch2}
            onPropertiesSearch2Change={(value) => {
              setEventsPropertiesSearch2(value.trim().slice(0, 500));
              setEventsOffset(0);
            }}
            clientErrorBucket={eventsClientErrorBucket}
            interactionSessionId={eventsInteractionSessionId}
            onInteractionSessionIdChange={(value) => {
              const trimmed = value.trim().slice(0, 128);
              setEventsInteractionSessionId(trimmed);
              setEventsInteractionSessionCohort("");
              setEventsOffset(0);
              if (trimmed) {
                setEventsSort("client_timestamp");
                setEventsSortDir("asc");
              }
            }}
            interactionSessionCohort={eventsInteractionSessionCohort}
            onInteractionSessionCohortChange={(value) => {
              setEventsInteractionSessionCohort(value);
              setEventsInteractionSessionId("");
              setEventsOffset(0);
            }}
            showTemperatureEvents={eventsShowTemperature}
            onShowTemperatureEventsChange={(on) => {
              setEventsShowTemperature(on);
              setEventsOffset(0);
            }}
            showUptimePulse={eventsShowUptimePulse}
            onShowUptimePulseChange={(on) => {
              setEventsShowUptimePulse(on);
              setEventsOffset(0);
            }}
            hideAttractBasics={eventsHideAttractBasics}
            onHideAttractBasicsChange={(on) => {
              setEventsHideAttractBasics(on);
              setEventsOffset(0);
            }}
            dedupePopupTapMissed={eventsDedupePopupTapMissed}
            onDedupePopupTapMissedChange={(on) => {
              setEventsDedupePopupTapMissed(on);
              setEventsOffset(0);
            }}
            tempCelsiusMin={eventsTempCelsiusMin}
            tempCelsiusMaxExclusive={eventsTempCelsiusMaxExclusive}
            onClearTempCelsiusRange={() => {
              setEventsTempCelsiusMin(undefined);
              setEventsTempCelsiusMaxExclusive(undefined);
              setEventsOffset(0);
            }}
            sortBy={eventsSort}
            sortDir={eventsSortDir}
            onSortChange={(column, dir) => {
              setEventsSort(column);
              setEventsSortDir(dir);
              setEventsOffset(0);
            }}
            from={filters.from}
            to={filters.to}
          />
        )}
      </div>
    </div>
    </ReportingTimezoneProvider>
  );
}
